import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchHistoricalData } from '../services/apiService';
import { parseDdMmYyyyDateTime } from '../utils/dateTimeFormat';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
  ScatterChart,
  Scatter
} from 'recharts';
import './ReportGraphsPage.css';

const DEFAULT_REPORT_START_TIME = '00:00';
const DEFAULT_REPORT_END_TIME = '23:59';
const MINUTES_IN_DAY = 24 * 60;
const EMPTY_CHART_DATA = [];

function formatTimeForApiStart(htmlTime) {
  const t = String(htmlTime || '').trim();
  if (!t) return null;
  if (/^\d{2}:\d{2}:\d{2}$/.test(t)) return t;
  if (/^\d{2}:\d{2}$/.test(t)) return `${t}:00`;
  return `${t}:00`;
}

function formatTimeForApiEnd(htmlTime) {
  const t = String(htmlTime || '').trim();
  if (!t) return null;
  if (/^\d{2}:\d{2}:\d{2}$/.test(t)) {
    if (t === '23:59:00') return '23:59:59';
    return t;
  }
  if (/^\d{2}:\d{2}$/.test(t)) {
    if (t === '23:59') return '23:59:59';
    return `${t}:00`;
  }
  return `${t}:00`;
}

function localDateFromYmdAndHms(yyyyMmDd, hhmmss) {
  const [y, mo, d] = String(yyyyMmDd || '')
    .split('-')
    .map((x) => parseInt(x, 10));
  if (!y || !mo || !d) return null;
  const parts = String(hhmmss || '').split(':');
  const h = parseInt(parts[0], 10) || 0;
  const mi = parseInt(parts[1], 10) || 0;
  const s = parseInt(parts[2] || '0', 10) || 0;
  const dt = new Date(y, mo - 1, d, h, mi, s, 0);
  return isNaN(dt.getTime()) ? null : dt;
}

function getGraphWindowBounds(startDate, endDate, startTime, endTime) {
  const startD = startDate || endDate;
  const endD = endDate || startDate;
  if (!startD || !endD) return null;
  const startClock = (startTime || '').trim() || DEFAULT_REPORT_START_TIME;
  const endClock = (endTime || '').trim() || DEFAULT_REPORT_END_TIME;
  const from = localDateFromYmdAndHms(startD, formatTimeForApiStart(startClock));
  const to = localDateFromYmdAndHms(endD, formatTimeForApiEnd(endClock));
  if (!from || !to) return null;
  return { from, to };
}

function formatMinutesAxisTick(m) {
  const clamped = Math.max(0, Math.min(MINUTES_IN_DAY, Number(m)));
  const h = Math.floor(clamped / 60);
  const min = Math.floor(clamped % 60);
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** Empty time uses the same defaults as the API (full calendar day). */
function isFullCalendarDayTimeRange(startTime, endTime) {
  const rawSt = String(startTime ?? '').trim();
  const rawEt = String(endTime ?? '').trim();
  const st = rawSt || DEFAULT_REPORT_START_TIME;
  const et = rawEt || DEFAULT_REPORT_END_TIME;
  const startFull = st === '00:00' || st === '00:00:00';
  const endFull =
    et === '23:59' || et === '23:59:00' || et === '23:59:59' || et === '24:00' || et === '24:00:00';
  return startFull && endFull;
}

function computeSingleDayTimeDomain(singleDayRange, windowSlice) {
  if (!singleDayRange || !windowSlice || windowSlice.length === 0) return [0, MINUTES_IN_DAY];
  const mins = windowSlice.map((p) => p.minutesFromMidnight).filter((m) => m != null && !Number.isNaN(Number(m)));
  if (mins.length === 0) return [0, MINUTES_IN_DAY];
  const lo = Math.min(...mins);
  const hi = Math.max(...mins);
  const pad = Math.max(2, (hi - lo) * 0.08);
  return [Math.max(0, lo - pad), Math.min(MINUTES_IN_DAY, hi + pad)];
}

/** One point per clock hour (0–23): mean of all samples in that hour; X at mid-hour for the 24 h axis. */
function buildHourlyAverageSeries(series) {
  const pad2 = (x) => String(x).padStart(2, '0');
  const byHour = Array.from({ length: 24 }, () => []);
  for (const p of series) {
    if (p == null || p.minutesFromMidnight == null || Number.isNaN(Number(p.minutesFromMidnight))) continue;
    const h = Math.min(23, Math.max(0, Math.floor(Number(p.minutesFromMidnight) / 60)));
    byHour[h].push(p);
  }
  const avg = (pts, key) => {
    const vals = pts.map((row) => row[key]).filter((v) => v != null && !Number.isNaN(Number(v)));
    if (vals.length === 0) return null;
    const sum = vals.reduce((a, v) => a + Number(v), 0);
    return sum / vals.length;
  };
  return byHour.map((pts, h) => {
    const hNext = (h + 1) % 24;
    return {
      idx: h,
      hourSlot: h,
      hourAggregate: true,
      sampleCount: pts.length,
      minutesFromMidnight: h * 60 + 30,
      timeLabel: `${pad2(h)}:00–${pad2(hNext)}:00`,
      inverterR: avg(pts, 'inverterR'),
      inverterY: avg(pts, 'inverterY'),
      inverterB: avg(pts, 'inverterB'),
      inverterOut: avg(pts, 'inverterOut'),
      batteryV: avg(pts, 'batteryV'),
      recordedAt: null
    };
  });
}

const ReportGraphsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateDates = location.state || {};

  const [startDate, setStartDate] = useState(stateDates.startDate || '');
  const [endDate, setEndDate] = useState(stateDates.endDate || '');
  const [startTime, setStartTime] = useState(() => {
    if (stateDates.startTime != null && String(stateDates.startTime).trim() !== '') return String(stateDates.startTime).trim();
    if (stateDates.startDate || stateDates.endDate) return DEFAULT_REPORT_START_TIME;
    return '';
  });
  const [endTime, setEndTime] = useState(() => {
    if (stateDates.endTime != null && String(stateDates.endTime).trim() !== '') return String(stateDates.endTime).trim();
    if (stateDates.startDate || stateDates.endDate) return DEFAULT_REPORT_END_TIME;
    return '';
  });
  const [graphData, setGraphData] = useState([]);
  const [selectedDayTitle, setSelectedDayTitle] = useState('');
  const [graphLoading, setGraphLoading] = useState(false);
  const [graphError, setGraphError] = useState(null);
  /** Each chart has its own X (time) window: indices into `baseChartData`. */
  const [invRange, setInvRange] = useState({ startIndex: 0, endIndex: 0 });
  const [battRange, setBattRange] = useState({ startIndex: 0, endIndex: 0 });
  const [scatterRange, setScatterRange] = useState({ startIndex: 0, endIndex: 0 });
  const [chartHeight, setChartHeight] = useState(380);

  useEffect(() => {
    const updateHeight = () => {
      const w = window.innerWidth;
      setChartHeight(w <= 360 ? 220 : w <= 480 ? 260 : w <= 768 ? 300 : 380);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const today = new Date().toISOString().split('T')[0];

  const singleDayRange = Boolean(startDate && startDate === (endDate || startDate));

  /** Single day + full-day time filters → 24 hourly averages; any narrower time range → every sample. */
  const useHourlyAggregation = useMemo(
    () => singleDayRange && isFullCalendarDayTimeRange(startTime, endTime),
    [singleDayRange, startTime, endTime]
  );

  const baseChartData = useMemo(() => {
    if (!graphData.length) return EMPTY_CHART_DATA;
    if (!useHourlyAggregation) return graphData;
    return buildHourlyAverageSeries(graphData);
  }, [graphData, useHourlyAggregation]);

  const n = baseChartData.length;

  const invChartWindow = useMemo(
    () => (n > 0 ? baseChartData.slice(invRange.startIndex, invRange.endIndex + 1) : []),
    [n, baseChartData, invRange.startIndex, invRange.endIndex]
  );
  const battChartWindow = useMemo(
    () => (n > 0 ? baseChartData.slice(battRange.startIndex, battRange.endIndex + 1) : []),
    [n, baseChartData, battRange.startIndex, battRange.endIndex]
  );
  const scatterChartWindow = useMemo(
    () => (n > 0 ? baseChartData.slice(scatterRange.startIndex, scatterRange.endIndex + 1) : []),
    [n, baseChartData, scatterRange.startIndex, scatterRange.endIndex]
  );

  const invTimeDomain = useMemo(
    () => computeSingleDayTimeDomain(singleDayRange, invChartWindow),
    [singleDayRange, invChartWindow]
  );
  const battTimeDomain = useMemo(
    () => computeSingleDayTimeDomain(singleDayRange, battChartWindow),
    [singleDayRange, battChartWindow]
  );

  const invSpan = n > 0 ? invRange.endIndex - invRange.startIndex + 1 : 0;
  const battSpan = n > 0 ? battRange.endIndex - battRange.startIndex + 1 : 0;
  const scatterSpan = n > 0 ? scatterRange.endIndex - scatterRange.startIndex + 1 : 0;

  const parseNum = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const x = typeof v === 'number' ? v : parseFloat(v);
    return Number.isFinite(x) ? x : null;
  };

  const parseApiTime = (raw) => parseDdMmYyyyDateTime(raw);

  const formatDayTitle = (yyyyMmDd) => {
    const s = String(yyyyMmDd || '').trim();
    if (!s) return '';
    const d = new Date(`${s}T00:00:00`);
    if (isNaN(d.getTime())) return s;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const day = d.toLocaleDateString(undefined, { weekday: 'short' });
    return `${day} ${dd}-${mm}-${yyyy}`;
  };

  const buildSeriesInWindow = (rows, sd, ed, st, et) => {
    const bounds = getGraphWindowBounds(sd, ed || sd, st, et);
    if (!bounds) return [];
    const isSingleDay = sd === (ed || sd);
    const dayStart = isSingleDay ? new Date(`${sd}T00:00:00`) : null;

    const inRange = (Array.isArray(rows) ? rows : [])
      .map((r) => {
        // Match report table: use server recordedAt first so seconds (e.g. 00:00:58) drive the axis.
        const t = parseApiTime(r?.time) ?? parseApiTime(r?.recordedAt);
        return { r, t };
      })
      .filter(({ t }) => t && !isNaN(t.getTime()) && t >= bounds.from && t <= bounds.to)
      .sort((a, b) => a.t.getTime() - b.t.getTime());

    return inRange.map(({ r, t }, idx) => {
      const invR = parseNum(r?.inverter?.phaseR);
      const invY = parseNum(r?.inverter?.phaseY);
      const invB = parseNum(r?.inverter?.phaseB);
      const batt = parseNum(r?.batteryVoltage);
      const invOut =
        invR != null && invY != null && invB != null ? (invR + invY + invB) / 3 : (invR ?? invY ?? invB ?? null);

      const hh = String(t.getHours()).padStart(2, '0');
      const mm = String(t.getMinutes()).padStart(2, '0');
      const ss = String(t.getSeconds()).padStart(2, '0');
      const minutesFromMidnight =
        isSingleDay && dayStart && !isNaN(dayStart.getTime()) ? (t.getTime() - dayStart.getTime()) / 60000 : null;

      return {
        idx,
        timeLabel: `${hh}:${mm}:${ss}`,
        minutesFromMidnight,
        inverterR: invR,
        inverterY: invY,
        inverterB: invB,
        inverterOut: invOut,
        batteryV: batt,
        recordedAt: t.toISOString()
      };
    });
  };

  const resetChartXFor = (setRange) => {
    if (n <= 0) return;
    setRange({ startIndex: 0, endIndex: n - 1 });
  };

  /** Narrow time window along X (fewer points visible). */
  const zoomInXAxisFor = (range, setRange) => {
    if (n <= 1) return;
    const span = range.endIndex - range.startIndex + 1;
    if (span <= 1) return;
    const newSpan = Math.max(1, span - 1);
    const center = (range.startIndex + range.endIndex) / 2;
    const start = Math.max(0, Math.floor(center - (newSpan - 1) / 2));
    const end = Math.min(n - 1, start + newSpan - 1);
    setRange({ startIndex: start, endIndex: end });
  };

  /** Widen time window along X. */
  const zoomOutXAxisFor = (range, setRange) => {
    if (n <= 0) return;
    const span = range.endIndex - range.startIndex + 1;
    const newSpan = Math.min(n, span + 1);
    const center = (range.startIndex + range.endIndex) / 2;
    const start = Math.max(0, Math.floor(center - (newSpan - 1) / 2));
    const end = Math.min(n - 1, start + newSpan - 1);
    setRange({ startIndex: start, endIndex: end });
  };

  const loadGraphData = async () => {
    if (!startDate || !endDate) {
      setGraphError('Please select both start and end dates.');
      return;
    }
    setGraphLoading(true);
    setGraphError(null);
    try {
      const endD = endDate || startDate;
      const isSingleDay = startDate === endD;
      const params = { startDate, endDate: endD, limit: isSingleDay ? 0 : 500 };
      const stClock = (startTime || '').trim() || DEFAULT_REPORT_START_TIME;
      const etClock = (endTime || '').trim() || DEFAULT_REPORT_END_TIME;
      const st = formatTimeForApiStart(stClock);
      const et = formatTimeForApiEnd(etClock);
      if (startDate && st) params.startTime = st;
      if (endD && et) params.endTime = et;
      const data = await fetchHistoricalData(params);
      setSelectedDayTitle(formatDayTitle(startDate));
      const seriesAll = buildSeriesInWindow(
        Array.isArray(data) ? data : [],
        startDate,
        endD,
        startTime,
        endTime
      );
      setGraphData(seriesAll);
    } catch (err) {
      console.error(err);
      setGraphError('Failed to load graph data. Please check your connection.');
    } finally {
      setGraphLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate && (stateDates.startDate || graphData.length > 0 || !graphError)) {
      loadGraphData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, startTime, endTime]);

  useEffect(() => {
    const len = baseChartData.length;
    if (len > 0) {
      const r = { startIndex: 0, endIndex: len - 1 };
      setInvRange(r);
      setBattRange(r);
      setScatterRange(r);
    }
  }, [baseChartData]);

  const formatTooltipValue = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '0';
    return parseFloat(value).toFixed(2);
  };

  const timeAxisTooltipLabel = (_, payload) => {
    const p = payload && payload[0] && payload[0].payload ? payload[0].payload : null;
    if (p?.hourAggregate && p.sampleCount != null)
      return `Time: ${p.timeLabel} (avg, ${p.sampleCount} sample${p.sampleCount === 1 ? '' : 's'})`;
    if (p?.timeLabel) return `Time: ${p.timeLabel}`;
    return 'Time';
  };

  return (
    <div className="report-graphs-page">
      <div className="report-graphs-container">
        <div className="report-graphs-header">
          <h2 className="report-graphs-title">Data Visualization</h2>
          <div className="report-graphs-actions">
            <button
              type="button"
              onClick={() =>
                navigate('/report', {
                  state: {
                    startDate,
                    endDate: endDate || startDate,
                    startTime: (startTime || '').trim() || DEFAULT_REPORT_START_TIME,
                    endTime: (endTime || '').trim() || DEFAULT_REPORT_END_TIME
                  }
                })
              }
              className="btn-back-report"
            >
              ← Back to Report
            </button>
            <button type="button" onClick={() => navigate('/')} className="btn-back-dashboard">
              Dashboard
            </button>
          </div>
        </div>

        <div className="graph-filters">
          <h3 className="filter-section-title">DATE &amp; TIME</h3>
          <div className="filter-row">
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  const v = e.target.value;
                  setStartDate(v);
                  if (v) {
                    if (!endDate) setEndDate(v);
                    if (!(String(startTime || '').trim())) setStartTime(DEFAULT_REPORT_START_TIME);
                    if (!(String(endTime || '').trim())) setEndTime(DEFAULT_REPORT_END_TIME);
                  } else {
                    setStartTime('');
                  }
                }}
                max={today}
                className="form-input"
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  const v = e.target.value;
                  setEndDate(v);
                  if (v) {
                    if (!startDate) setStartDate(v);
                    if (!(String(endTime || '').trim())) setEndTime(DEFAULT_REPORT_END_TIME);
                    if (!(String(startTime || '').trim())) setStartTime(DEFAULT_REPORT_START_TIME);
                  } else {
                    setEndTime('');
                  }
                }}
                max={today}
                min={startDate || undefined}
                className="form-input"
              />
            </div>
            <div className="filter-group">
              <label>Start Time</label>
              <input
                type="time"
                value={startTime}
                title="00:00 is midnight (12 AM)"
                onChange={(e) => setStartTime(e.target.value)}
                className="form-input"
                placeholder="--:--"
              />
            </div>
            <div className="filter-group">
              <label>End Time</label>
              <input
                type="time"
                value={endTime}
                title="23:59 is 11:59 PM; range ends at 23:59:59"
                onChange={(e) => setEndTime(e.target.value)}
                className="form-input"
                placeholder="--:--"
              />
            </div>
            <button type="button" onClick={loadGraphData} disabled={graphLoading || !startDate || !endDate} className="btn-load-graphs">
              {graphLoading ? 'Loading...' : 'Load Graph'}
            </button>
          </div>
        </div>

        {graphError && <div className="graph-error-msg">{graphError}</div>}
        {!graphLoading && !graphError && graphData.length === 0 && startDate && endDate && (
          <div className="graph-no-data">No data available for the selected date range.</div>
        )}

        {!graphLoading && !graphError && graphData.length > 0 && (
          <div className="graphs-container">
            <div className="graph-card">
              <div className="graph-card-header">
                <h4 className="graph-card-title">
                  Inverter (3-Phase) Voltage vs Time{singleDayRange ? ' (24 h)' : ''} — {selectedDayTitle}
                  {useHourlyAggregation ? ' — hourly averages' : ''}
                </h4>
                <div className="graph-zoom-controls">
                  <button
                    type="button"
                    className="btn-zoom"
                    onClick={() => zoomInXAxisFor(invRange, setInvRange)}
                    disabled={n <= 1 || invSpan <= 1}
                    title="Narrow time range on this chart (X)"
                  >
                    Zoom in X
                  </button>
                  <button
                    type="button"
                    className="btn-zoom"
                    onClick={() => zoomOutXAxisFor(invRange, setInvRange)}
                    disabled={n <= 0 || invSpan >= n}
                    title="Widen time range on this chart (X)"
                  >
                    Zoom out X
                  </button>
                  <button type="button" className="btn-zoom" onClick={() => resetChartXFor(setInvRange)} title="Reset time range for this chart">
                    Reset X
                  </button>
                </div>
              </div>
              <div className="graph-scroll-wrapper">
                <ResponsiveContainer width="100%" height={chartHeight} minWidth={0} className="graph-responsive-chart">
                  <LineChart data={invChartWindow} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    {singleDayRange ? (
                      <XAxis
                        type="number"
                        dataKey="minutesFromMidnight"
                        domain={invTimeDomain}
                        tickFormatter={formatMinutesAxisTick}
                        label={{ value: 'Time (24 h)', position: 'insideBottom', offset: -4 }}
                      />
                    ) : (
                      <XAxis dataKey="timeLabel" />
                    )}
                    <YAxis />
                    <Tooltip formatter={formatTooltipValue} labelFormatter={timeAxisTooltipLabel} />
                    <Legend />
                    <Line type="monotone" dataKey="inverterR" stroke="#be1616" strokeWidth={2} name="Inv R" connectNulls />
                    <Line type="monotone" dataKey="inverterY" stroke="#ffee02" strokeWidth={2} name="Inv Y" connectNulls />
                    <Line type="monotone" dataKey="inverterB" stroke="#3f0ea8" strokeWidth={2} name="Inv B" connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="graph-brush-wrapper">
                <ResponsiveContainer width="100%" height={chartHeight <= 260 ? 44 : 56}>
                  <LineChart data={baseChartData} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                    {singleDayRange ? (
                      <XAxis type="number" dataKey="minutesFromMidnight" domain={[0, MINUTES_IN_DAY]} hide />
                    ) : (
                      <XAxis dataKey="timeLabel" hide />
                    )}
                    <YAxis hide />
                    <Line type="monotone" dataKey="inverterR" stroke="#3182ce" strokeWidth={1} dot={false} connectNulls />
                    <Line type="monotone" dataKey="inverterY" stroke="#38a169" strokeWidth={1} dot={false} connectNulls />
                    <Line type="monotone" dataKey="inverterB" stroke="#805ad5" strokeWidth={1} dot={false} connectNulls />
                    <Brush
                      dataKey={singleDayRange ? 'minutesFromMidnight' : 'timeLabel'}
                      height={24}
                      stroke="#3182ce"
                      fill="#e6f2ff"
                      startIndex={invRange.startIndex}
                      endIndex={invRange.endIndex}
                      onChange={(range) => {
                        const start = typeof range?.startIndex === 'number' ? range.startIndex : (Array.isArray(range) ? range[0] : 0);
                        const end = typeof range?.endIndex === 'number' ? range.endIndex : (Array.isArray(range) ? range[1] : n - 1);
                        setInvRange({
                          startIndex: Math.max(0, start),
                          endIndex: Math.min(Math.max(0, n - 1), end)
                        });
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="graph-hint">
                One day with start 00:00 and end 23:59 shows 24 hourly averages; a narrower time filter shows every sample in that range. Zoom/brush here only affect this chart.
              </p>
            </div>

            <div className="graph-card">
              <div className="graph-card-header">
                <h4 className="graph-card-title">
                  Battery Voltage vs Time{singleDayRange ? ' (24 h)' : ''} — {selectedDayTitle}
                  {useHourlyAggregation ? ' — hourly averages' : ''}
                </h4>
                <div className="graph-zoom-controls">
                  <button
                    type="button"
                    className="btn-zoom"
                    onClick={() => zoomInXAxisFor(battRange, setBattRange)}
                    disabled={n <= 1 || battSpan <= 1}
                    title="Narrow time range on this chart (X)"
                  >
                    Zoom in X
                  </button>
                  <button
                    type="button"
                    className="btn-zoom"
                    onClick={() => zoomOutXAxisFor(battRange, setBattRange)}
                    disabled={n <= 0 || battSpan >= n}
                    title="Widen time range on this chart (X)"
                  >
                    Zoom out X
                  </button>
                  <button type="button" className="btn-zoom" onClick={() => resetChartXFor(setBattRange)} title="Reset time range for this chart">
                    Reset X
                  </button>
                </div>
              </div>
              <div className="graph-scroll-wrapper">
                <ResponsiveContainer width="100%" height={chartHeight} minWidth={0} className="graph-responsive-chart">
                  <LineChart data={battChartWindow} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    {singleDayRange ? (
                      <XAxis
                        type="number"
                        dataKey="minutesFromMidnight"
                        domain={battTimeDomain}
                        tickFormatter={formatMinutesAxisTick}
                        label={{ value: 'Time (24 h)', position: 'insideBottom', offset: -4 }}
                      />
                    ) : (
                      <XAxis dataKey="timeLabel" />
                    )}
                    <YAxis />
                    <Tooltip formatter={formatTooltipValue} labelFormatter={timeAxisTooltipLabel} />
                    <Legend />
                    <Line type="monotone" dataKey="batteryV" stroke="#dd6b20" strokeWidth={2} name="Battery V" connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="graph-brush-wrapper">
                <ResponsiveContainer width="100%" height={chartHeight <= 260 ? 44 : 56}>
                  <LineChart data={baseChartData} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                    {singleDayRange ? (
                      <XAxis type="number" dataKey="minutesFromMidnight" domain={[0, MINUTES_IN_DAY]} hide />
                    ) : (
                      <XAxis dataKey="timeLabel" hide />
                    )}
                    <YAxis hide />
                    <Line type="monotone" dataKey="batteryV" stroke="#dd6b20" strokeWidth={1} dot={false} connectNulls />
                    <Brush
                      dataKey={singleDayRange ? 'minutesFromMidnight' : 'timeLabel'}
                      height={24}
                      stroke="#3182ce"
                      fill="#e6f2ff"
                      startIndex={battRange.startIndex}
                      endIndex={battRange.endIndex}
                      onChange={(range) => {
                        const start = typeof range?.startIndex === 'number' ? range.startIndex : (Array.isArray(range) ? range[0] : 0);
                        const end = typeof range?.endIndex === 'number' ? range.endIndex : (Array.isArray(range) ? range[1] : n - 1);
                        setBattRange({
                          startIndex: Math.max(0, start),
                          endIndex: Math.min(Math.max(0, n - 1), end)
                        });
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="graph-hint">Zoom and brush are independent from the inverter chart.</p>
            </div>

            <div className="graph-card">
              <div className="graph-card-header">
                <h4 className="graph-card-title">
                  Battery Voltage vs Inverter Output Voltage — {selectedDayTitle}
                  {useHourlyAggregation ? ' (hourly avg points)' : ''}
                </h4>
                <div className="graph-zoom-controls">
                  <button
                    type="button"
                    className="btn-zoom"
                    onClick={() => zoomInXAxisFor(scatterRange, setScatterRange)}
                    disabled={n <= 1 || scatterSpan <= 1}
                    title="Fewer points by time order (same series, X/Y unchanged)"
                  >
                    Zoom in X
                  </button>
                  <button
                    type="button"
                    className="btn-zoom"
                    onClick={() => zoomOutXAxisFor(scatterRange, setScatterRange)}
                    disabled={n <= 0 || scatterSpan >= n}
                    title="More points by time order"
                  >
                    Zoom out X
                  </button>
                  <button type="button" className="btn-zoom" onClick={() => resetChartXFor(setScatterRange)} title="Reset time filter for this chart">
                    Reset X
                  </button>
                </div>
              </div>
              <div className="graph-scroll-wrapper">
                <ResponsiveContainer width="100%" height={chartHeight} minWidth={0} className="graph-responsive-chart">
                  <ScatterChart data={scatterChartWindow} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="inverterOut" name="Inverter Output V" type="number" />
                    <YAxis dataKey="batteryV" name="Battery V" type="number" />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} formatter={formatTooltipValue} labelFormatter={timeAxisTooltipLabel} />
                    <Legend />
                    <Scatter name="Battery vs Inverter Output" data={scatterChartWindow} fill="#2b6cb0" />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
              <div className="graph-brush-wrapper">
                <ResponsiveContainer width="100%" height={chartHeight <= 260 ? 44 : 56}>
                  <LineChart data={baseChartData} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                    {singleDayRange ? (
                      <XAxis type="number" dataKey="minutesFromMidnight" domain={[0, MINUTES_IN_DAY]} hide />
                    ) : (
                      <XAxis dataKey="timeLabel" hide />
                    )}
                    <YAxis hide />
                    <Line type="monotone" dataKey="batteryV" stroke="#2b6cb0" strokeWidth={1} dot={false} connectNulls />
                    <Brush
                      dataKey={singleDayRange ? 'minutesFromMidnight' : 'timeLabel'}
                      height={24}
                      stroke="#3182ce"
                      fill="#e6f2ff"
                      startIndex={scatterRange.startIndex}
                      endIndex={scatterRange.endIndex}
                      onChange={(range) => {
                        const start = typeof range?.startIndex === 'number' ? range.startIndex : (Array.isArray(range) ? range[0] : 0);
                        const end = typeof range?.endIndex === 'number' ? range.endIndex : (Array.isArray(range) ? range[1] : n - 1);
                        setScatterRange({
                          startIndex: Math.max(0, start),
                          endIndex: Math.min(Math.max(0, n - 1), end)
                        });
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="graph-hint">Inverter Output = average of Inv R/Y/B when all 3 are present; otherwise the first available phase. Time brush filters which points are shown.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportGraphsPage;
